/**
 * Client-side image compression to avoid 502/timeout on large photo uploads.
 * - Only touches images (leaves videos/audio/pdf untouched).
 * - Downscales to a max side (default 1920px) and re-encodes as JPEG 0.82.
 * - Skips compression if file is already small (<= 1.2MB) or not an image.
 * Returns a File (or Blob wrapped as File) ready for FormData.
 */
export async function compressImageIfNeeded(file, opts = {}) {
  const maxSide = opts.maxSide || 1920;
  const quality = opts.quality || 0.82;
  const skipUnder = opts.skipUnder || 1.2 * 1024 * 1024;
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file; // preserve animation
  if (file.size <= skipUnder) return file;

  try {
    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = dataUrl;
    });
    let { width, height } = img;
    if (width <= maxSide && height <= maxSide && file.size <= 2.5 * 1024 * 1024) {
      return file; // already reasonable
    }
    const scale = Math.min(1, maxSide / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    // Prefer the compressed one only if it's actually smaller
    if (blob.size >= file.size) return file;
    const base = (file.name || "photo").replace(/\.[a-z0-9]+$/i, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}
